import { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon";
import { api } from "../lib/api";
import { useSession } from "../lib/session";
import { useToast } from "../lib/toast";
import { formatPercent, pluralise } from "../lib/format";
import type { QuizSubmission } from "../lib/types";
import { useModuleCtx } from "./ModuleShell";

export default function QuizView() {
  const { content, progress, reloadModuleProgress } = useModuleCtx();
  const { reloadProgress } = useSession();
  const toast = useToast();
  const { quiz, module } = content;
  const base = `/modules/${module.id}`;

  const [answers, setAnswers] = useState<Record<string, number[]>>({});
  const [result, setResult] = useState<QuizSubmission | null>(null);
  const [busy, setBusy] = useState(false);
  const resultRef = useRef<HTMLDivElement | null>(null);

  const resultsById = useMemo(() => {
    const map = new Map<string, QuizSubmission["results"][number]>();
    for (const row of result?.results ?? []) map.set(row.questionId, row);
    return map;
  }, [result]);

  const answered = quiz.questions.filter((question) => (answers[question.id] ?? []).length > 0);
  const remaining = quiz.questions.length - answered.length;
  const locked = result !== null;

  const toggle = useCallback((questionId: string, optionIndex: number, multi: boolean) => {
    setAnswers((previous) => {
      const current = previous[questionId] ?? [];
      if (!multi) return { ...previous, [questionId]: [optionIndex] };
      const next = current.includes(optionIndex)
        ? current.filter((value) => value !== optionIndex)
        : [...current, optionIndex].sort((a, b) => a - b);
      return { ...previous, [questionId]: next };
    });
  }, []);

  const submit = useCallback(async () => {
    setBusy(true);
    try {
      const submission = await api.submitQuiz(quiz.id, answers);
      setResult(submission);
      await Promise.all([reloadModuleProgress(), reloadProgress()]);
      // Put the outcome in front of the learner rather than leaving them at the last question.
      requestAnimationFrame(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        resultRef.current?.focus();
      });
    } catch (error) {
      toast.reportError(error, "Your answers could not be submitted.");
    } finally {
      setBusy(false);
    }
  }, [answers, quiz.id, reloadModuleProgress, reloadProgress, toast]);

  const retry = useCallback(() => {
    setResult(null);
    setAnswers({});
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="reader">
      <header className="reader-head">
        <h1>{quiz.title}</h1>
        <div className="reader-meta">
          <span>{pluralise(quiz.questions.length, "question")}</span>
          <span>{formatPercent(quiz.passMark)} to pass</span>
          {progress.quiz ? (
            <span>
              Best so far {formatPercent(progress.quiz.bestScore)} over{" "}
              {pluralise(progress.quiz.attempts, "attempt")}
            </span>
          ) : (
            <span>Your first attempt</span>
          )}
        </div>
      </header>

      <div className="quiz">
        <div
          className="quiz-progress"
          role="img"
          aria-label={
            result
              ? `${result.correct} of ${result.total} questions correct`
              : `${answered.length} of ${quiz.questions.length} questions answered`
          }
        >
          {quiz.questions.map((question) => {
            const row = resultsById.get(question.id);
            const state = row
              ? row.correct ? "is-correct" : "is-wrong"
              : (answers[question.id] ?? []).length > 0 ? "is-answered" : "";
            return <span key={question.id} className={`quiz-pip ${state}`} />;
          })}
        </div>

        <div ref={resultRef} tabIndex={-1} aria-live="polite">
          {result ? (
            <div className={result.passed ? "score-banner is-pass" : "score-banner is-fail"}>
              <p className="score-figure">{formatPercent(result.score)}</p>
              <div>
                <h2>{result.passed ? "Passed" : "Not passed yet"}</h2>
                <p>
                  {result.correct} of {result.total} right. You need{" "}
                  {formatPercent(result.passMark)} to pass
                  {result.passed
                    ? ". The quiz gate for this module is now green."
                    : ". Read the explanations below, then try again — attempts are unlimited."}
                </p>
              </div>
              <div className="stack-tight">
                <button type="button" className="btn" onClick={retry}>
                  <Icon name="refresh" />
                  Try again
                </button>
                {result.passed ? (
                  <Link className="btn btn-primary" to={`${base}/lab`}>
                    On to the lab
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (!locked && remaining === 0) void submit();
          }}
        >
          {quiz.questions.map((question, questionIndex) => {
            const multi = question.type === "multi";
            const picked = answers[question.id] ?? [];
            const row = resultsById.get(question.id);
            return (
              <fieldset className="question" key={question.id}>
                <legend className="sr-only">
                  Question {questionIndex + 1} of {quiz.questions.length}
                </legend>
                <div className="question-head">
                  <span className="qnum" aria-hidden="true">
                    {String(questionIndex + 1).padStart(2, "0")}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div
                      className="question-prompt prose"
                      style={{ maxWidth: "none", fontSize: "1rem" }}
                      dangerouslySetInnerHTML={{ __html: question.promptHtml }}
                    />
                    <p className="question-kind">
                      {multi ? "Select all that apply." : "Select one answer."}
                    </p>
                  </div>
                  {row ? (
                    <span className={row.correct ? "mark mark-done" : "mark mark-fail"}>
                      <Icon name={row.correct ? "check" : "cross"} size={13} className="glyph" />
                      {row.correct ? "Correct" : "Wrong"}
                    </span>
                  ) : null}
                </div>

                <div className="options">
                  {question.options.map((option, optionIndex) => {
                    const isPicked = picked.includes(optionIndex);
                    const isAnswer = row?.answer.includes(optionIndex) ?? false;
                    const classes = ["option"];
                    if (locked) {
                      classes.push("is-locked");
                      if (isAnswer) classes.push("is-answer");
                      else if (isPicked) classes.push("is-wrong-pick");
                    } else if (isPicked) {
                      classes.push("is-picked");
                    }
                    return (
                      <label className={classes.join(" ")} key={optionIndex}>
                        <input
                          type={multi ? "checkbox" : "radio"}
                          name={question.id}
                          checked={isPicked}
                          disabled={locked}
                          onChange={() => toggle(question.id, optionIndex, multi)}
                        />
                        <span
                          dangerouslySetInnerHTML={{ __html: option }}
                        />
                      </label>
                    );
                  })}
                </div>

                {row ? (
                  <div
                    className={row.correct ? "explain is-correct" : "explain is-incorrect"}
                    dangerouslySetInnerHTML={{ __html: row.explanationHtml }}
                  />
                ) : null}
              </fieldset>
            );
          })}

          {locked ? (
            <div className="lesson-foot">
              <button type="button" className="btn" onClick={retry}>
                <Icon name="refresh" />
                Clear and try again
              </button>
              <div className="pager">
                <Link className="btn" to={base}>
                  <Icon name="arrowLeft" />
                  Module overview
                </Link>
                <Link className="btn btn-primary" to={`${base}/lab`}>
                  <Icon name="terminal" />
                  Open the lab
                </Link>
              </div>
            </div>
          ) : (
            <div className="lesson-foot">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={busy || remaining > 0}
              >
                {busy ? <span className="spinner" /> : null}
                {busy ? "Marking…" : "Submit answers"}
              </button>
              <p className="hint" aria-live="polite">
                {remaining > 0
                  ? `${pluralise(remaining, "question")} still to answer. Answers are only marked once you submit.`
                  : "All answered. Submit to see how you did."}
              </p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
